import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useAuthStore } from '@carouselgg/auth/store'
import { Avatar } from '@carouselgg/nexus/avatar'
import { Button } from '@carouselgg/nexus/button'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { toast } from '@carouselgg/nexus/toast'
import { CircleCheck } from '@carouselgg/nexus/icon'
import { getPass, scanPass, type PassInfo } from '../api/checkin'
import { returnTo } from './checkInCopy'
import styles from './CheckInPage.module.css'

type Load = { kind: 'loading' } | { kind: 'invalid' } | { kind: 'ready'; pass: PassInfo }

/**
 * What a player's check-in pass opens when scanned with a phone's own camera.
 * Staff for the tournament get the player's face and a Check in button — a
 * desk without the app can still scan passes. Anyone else is told to show it
 * at the desk.
 */
export function PassPage() {
  const { payload = '' } = useParams<{ payload: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isAuthLoading = useAuthStore((s) => s.isLoading)

  const [load, setLoad] = useState<Load>({ kind: 'loading' })
  const [saving, setSaving] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoad({ kind: 'ready', pass: await getPass(payload) })
    } catch {
      setLoad({ kind: 'invalid' })
    }
  }, [payload])

  useEffect(() => {
    if (isAuthLoading) return
    void refresh()
  }, [refresh, isAuthLoading, isAuthenticated])

  async function handleCheckIn(pass: PassInfo) {
    setSaving(true)
    try {
      const res = await scanPass(pass.season.org.id, payload)
      toast.success(res.already_checked_in ? `${res.player.display_name} was already checked in` : `${res.player.display_name} is checked in`)
      await refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check-in failed')
    } finally {
      setSaving(false)
    }
  }

  if (load.kind === 'loading') {
    return <div className={styles.page}><Text muted>Loading pass…</Text></div>
  }

  if (load.kind === 'invalid') {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">This isn't a valid pass</Text>
            <Text muted>Ask the player to open their pass in the Carousel app again.</Text>
          </div>
        </Card>
      </div>
    )
  }

  const { pass } = load
  const { player } = pass

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.content}>
          <Text size="sm" muted>{pass.season.org.name} · {pass.season.name}</Text>
          <Avatar src={player.avatar_url} fallback={player.display_name[0] ?? '?'} size="lg" />
          <Text as="h1" size="xl" weight="bold">
            {player.username
              ? <Link to={`/u/${encodeURIComponent(player.username)}`} className={styles.profileLink}>{player.display_name}</Link>
              : player.display_name}
          </Text>
          <Text size="sm" muted>@{player.username}{pass.team ? ` · ${pass.team.name}` : ' · Free agent'}</Text>

          {pass.checked_in_at ? (
            <div className={styles.done}>
              <CircleCheck size={40} className={styles.doneIcon} />
              <Text size="lg" weight="semibold">Checked in</Text>
            </div>
          ) : pass.expired ? (
            <Text muted>This code is old — it may be a screenshot. Ask them to show the live pass on their phone and scan again.</Text>
          ) : pass.can_check_in ? (
            <Button variant="primary" size="lg" className={styles.checkIn} disabled={saving} onClick={() => void handleCheckIn(pass)}>
              {saving ? 'Checking in…' : `Check in ${player.display_name}`}
            </Button>
          ) : isAuthenticated ? (
            <Text muted>Show this pass at the desk to check in.</Text>
          ) : (
            <>
              <Text size="sm" muted>Show this pass at the desk to check in. Running the desk? Sign in to check players in.</Text>
              <Button variant="secondary" onClick={() => navigate(`/login${returnTo(`/pass/${payload}`)}`)}>Staff sign in</Button>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
